import { dlog } from '../utils/debug';
import React, { useState, useEffect } from 'react';
import MatchEditor from './MatchEditor';
import { useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useMatchFollowing } from '../hooks/useMatchFollowing';
import { DynamicFlapperUnifiedLogo } from '../components';
import { isFeatureEnabled } from '../config/features';

function MatchViewer() {
  const { matchId } = useParams();
  const { user, loading: authLoading, ensureAnonymousUser } = useAuth();

  // Live viewer count needs a Firebase UID per visitor to write their
  // presence row. Lazily anon-sign-in viewers when the feature is on
  // and they don't have a real session yet. Silent — no UI change.
  //
  // CRITICAL: wait for auth to finish resolving before lazy-anon-auth.
  // Otherwise a real signed-in user landing here during the brief
  // Firebase Auth rehydration window gets their session overwritten
  // by a fresh anonymous sign-in (the user state and auth.currentUser
  // are both transiently null, our checks pass, signInAnonymously
  // wins the race).
  useEffect(() => {
    if (authLoading) return;
    if (!isFeatureEnabled('VIEWER_PRESENCE', user)) return;
    if (user) return;
    ensureAnonymousUser();
  }, [user, authLoading, ensureAnonymousUser]);
  
  dlog('🔍 MatchViewer component loaded with matchId:', matchId);
  const [viewerSet, setViewerSet] = useState(1);
  const [manualViewerSet, setManualViewerSet] = useState(null);
  const [userSettings, setUserSettings] = useState(null);

  const [matchState, setMatchState] = useState({
    teamAScore: 0,
    teamBScore: 0,
    allSetScores: [],
    teamASetsWon: 0,
    teamBSetsWon: 0,
    currentSet: 1,
  });
  const [teamAName, setTeamAName] = useState('Team A');
  const [teamBName, setTeamBName] = useState('Team B');
  const [teamAColor, setTeamAColor] = useState('#AE1F24');
  const [teamBColor, setTeamBColor] = useState('#1E96A7');
  const [hydrated, setHydrated] = useState(false);

  // Load user notification settings (only if user is logged in)
  useEffect(() => {
    if (!user) return;

    const loadSettings = async () => {
      try {
        const settingsRef = ref(db, `users/${user.uid}/settings`);
        const snapshot = await get(settingsRef);
        if (snapshot.exists()) {
          setUserSettings(snapshot.val());
        }
      } catch (error) {
        console.error('Error loading user settings:', error);
      }
    };

    loadSettings();
  }, [user]);

  // Load match data from Firebase
  useEffect(() => {
    if (!matchId) return;

    const matchRef = ref(db, `matches/${matchId}`);
    get(matchRef)
      .then(snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          dlog('🔍 MatchViewer loaded data:', data);
          
          // Load team data
          const teamAName = data.teamA?.name || "Team A";
          const teamBName = data.teamB?.name || "Team B";
          
          
          setTeamAName(teamAName);
          setTeamBName(teamBName);
          setTeamAColor(data.teamA?.color || "#AE1F24");
          setTeamBColor(data.teamB?.color || "#1E96A7");
          
          dlog('🔍 Team A:', data.teamA, 'Team B:', data.teamB);
          
          setMatchState({
            teamAScore: data.score?.teamA ?? 0,
            teamBScore: data.score?.teamB ?? 0,
            allSetScores: Array.isArray(data.sets) ? data.sets : [{ teamA: 0, teamB: 0 }],
            teamASetsWon: data.setsWon?.teamA ?? 0,
            teamBSetsWon: data.setsWon?.teamB ?? 0,
            currentSet: data.setNumber || 1,
          });
          
          setHydrated(true);
        } else {
          console.warn("Match not found:", matchId);
          setHydrated(true); // Still set hydrated to show "not found" state
        }
      })
      .catch(err => {
        console.error("Error loading match:", err);
        setHydrated(true);
      });
  }, [matchId]);

  // Auto-follow this match for notifications (only if user is logged in)
  // Temporarily disabled to fix match creation issue
  // useMatchFollowing(matchId, user, userSettings);

  const {
    teamAScore,
    teamBScore,
    currentSet,
    allSetScores,
    teamASetsWon,
    teamBSetsWon,
  } = matchState;

  function computeSetsWon(sets) {
    return sets.reduce(
      (acc, set) => {
        if (set.teamA > set.teamB) acc.teamA += 1;
        else if (set.teamB > set.teamA) acc.teamB += 1;
        return acc;
      },
      { teamA: 0, teamB: 0 }
    );
  }

  // 🧠 Auto-follow latest set unless viewer has manually navigated
  useEffect(() => {
    if (manualViewerSet === null) {
      setViewerSet(currentSet);
    }
  }, [currentSet, manualViewerSet]);

  const isLiveViewing = viewerSet === currentSet;

  // ✅ Show per-set score based on viewer navigation
  const viewerScore = isLiveViewing
    ? { teamA: teamAScore, teamB: teamBScore }
    : allSetScores[viewerSet - 1] || { teamA: 0, teamB: 0 };

  // ✅ Sets won are always totals across all sets (not viewerSet slice)
  const viewerSetsWon = computeSetsWon(allSetScores);

  // Save match to user's viewed matches (signed in) or localStorage (guest)
  useEffect(() => {
    if (!matchId) return;

    if (user) {
      // Signed in: save to Firebase
      const saveViewedMatch = async () => {
        try {
          const viewedRef = ref(db, `users/${user.uid}/viewedMatches/${matchId}`);
          await set(viewedRef, {
            viewedAt: Date.now()
          });
          dlog('✅ Saved viewed match to Firebase:', matchId);
        } catch (error) {
          console.error('❌ Error saving viewed match:', error);
        }
      };

      saveViewedMatch();
    } else {
      // Guest: save to localStorage
      try {
        const guestMatches = JSON.parse(localStorage.getItem('guestViewedMatches') || '{}');
        guestMatches[matchId] = {
          viewedAt: Date.now()
        };
        localStorage.setItem('guestViewedMatches', JSON.stringify(guestMatches));
        dlog('✅ Saved viewed match to localStorage:', matchId);
      } catch (error) {
        console.error('❌ Error saving guest match to localStorage:', error);
      }
    }
  }, [matchId, user]);

  const hydratedData = {
    teamAScore,
    teamBScore,
    allSetScores,
    teamASetsWon,
    teamBSetsWon,
    currentSet,
    teamAName,
    teamBName,
    teamAColor,
    teamBColor,
  };
  

  return (
    <MatchEditor
      readOnly
      matchId={matchId}
      hydratedData={hydratedData}
      viewerSet={viewerSet}
      setViewerSet={setViewerSet}
      viewerScore={viewerScore}
      viewerSetsWon={viewerSetsWon}
      currentSet={currentSet}
      allSetScores={allSetScores}
      setMatchState={setMatchState}
      manualViewerSet={manualViewerSet}
      setManualViewerSet={setManualViewerSet}
      isDataLoading={!hydrated}
    />
  );
}

export default MatchViewer;
